/**
 * Toast copy for HITL decision actions (CT-5096 — Continuation / Handoff /
 * Question / Break Glass).
 *
 * Errors thrown by {@link resolveHitlTask} carry (see hitlActions.js):
 *   - `.code` — the HITL `error_code` (HITL_ERR_TASK_NOT_FOUND,
 *     HITL_ERR_DECISION_INVALID, HITL_ERR_INVALID_REQUEST, HITL_ERR_RESUME_FAILED,
 *     HITL_ERR_UNEXPECTED) or a client-side guard code (IDS_MISSING).
 *   - `.status` — the REAL HTTP status (400 / 404 / 502 …), unlike the GS families
 *     where 200 always wraps the outcome.
 *
 * All copy lives under `hitl.*`; callers pass the footer kind for a sensible fallback.
 */

/** HITL error_code / guard code → locale key prefix under `hitl.*`. */
const HITL_ERROR_KEYS = {
  HITL_ERR_TASK_NOT_FOUND: 'errorNotFound',
  HITL_ERR_INVALID_REQUEST: 'errorValidation',
  HITL_ERR_DECISION_INVALID: 'errorValidation',
  HITL_ERR_RESUME_FAILED: 'errorResume',
  HITL_ERR_UNEXPECTED: 'errorServer',
  // Client-side guard (no request made — event lacked contract_id).
  IDS_MISSING: 'errorIdsMissing',
};

/**
 * @param {import('@aimsos/i18n').TFunction} t
 * @param {Error & { code?: string, status?: number }} [error]
 * @param {'continuation'|'handoff'|'question'|'breakGlass'} [kind]
 * @returns {{ title: string, message: string }}
 */
export function hitlActionErrorToast(t, error, kind = 'continuation') {
  const code = error?.code;
  const keyPrefix = code && HITL_ERROR_KEYS[code];
  if (keyPrefix) {
    return { title: t(`hitl.${keyPrefix}Title`), message: t(`hitl.${keyPrefix}Message`) };
  }
  // Resume/backend failure surfaced as a bare 5xx (e.g. LangGraph 500 on resume).
  if (error?.status != null && error.status >= 500) {
    return { title: t('hitl.errorServerTitle'), message: t('hitl.errorServerMessage') };
  }
  return {
    title: t('hitl.actionFailedTitle'),
    message: error?.message || t('hitl.actionFailedMessage', { kind }),
  };
}
